import React, { useEffect, useRef, useState } from 'react';
import * as Player from './styles/player-style';
import { Cpds } from '@Cpds.Ui';
import { checkValue } from 'src/components/common/helper';
import { CpdsAspectRatioType, CpdsObjectFitType } from 'components/common/cpds/cpds-image/constants';
import { css } from 'styled-components';
import { ClickStreamEventType } from 'src/utils/event-wrapper';

interface VimeoPlayerProps {
  videoId: string;
  posterUrl?: string;
  playBtnColor?: string;
  playerOptions?: any;
  altText: string;
  trackClickVideo?: (arg: any) => void;
  eventCategory?: string;
  eventLocation?: string;
}

declare global {
  interface Window {
    Vimeo: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const VimeoPlayer: React.FC<VimeoPlayerProps> = ({
  videoId,
  playerOptions,
  posterUrl,
  playBtnColor,
  altText = 'Vimeo video',
  trackClickVideo,
  eventCategory,
  eventLocation
}) => {
  const playerRef = useRef<HTMLIFrameElement | null>(null);
  const [player, setPlayer] = useState<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showThumbnail, setShowThumbnail] = useState(true);

  useEffect(() => {
    const waitForVM = (attempts = 10) => {
      if (window.Vimeo && window.Vimeo.Player && playerRef.current) {
        const newPlayer = new window.Vimeo.Player(playerRef.current);
        setPlayer(newPlayer);

        newPlayer.on('play', function () {
          setIsPlaying(true);
        });

        newPlayer.on('pause', function () {
          setIsPlaying(false);
        });
      } else if (attempts > 0) {
        setTimeout(() => waitForVM(attempts - 1), 500);
      } else {
        console.error('Vimeo API failed to load.');
      }
    };

    if (!window.Vimeo) {
      const tag = document.createElement('script');
      tag.src = 'https://player.vimeo.com/api/player.js';
      document.body.appendChild(tag);
    }

    waitForVM();

    return () => {
      if (player) player.destroy();
    };
  }, [videoId, playerOptions]);

  const handleTogglePlay = () => {
    if (isPlaying) {
      player?.pause();
    } else {
      player?.play();
      setShowThumbnail(false);
    }

    trackClickstream();
  };

  const trackClickstream = () => {
    if (trackClickVideo && player) {
      player.getDuration().then((videoDuration) => {
        player.getVideoUrl().then((videoUrl) => {
          const videoTitle = videoUrl.split('/').pop() || 'Unknown Title';
  
          const clickstreamDataImage = {
            EVENT_NAME: isPlaying ? 'video_play.selected' : 'video_pause.selected', // Corrected condition
            EVENT_TYPE: ClickStreamEventType.INTERACTION,
            EVENT_CATEGORY: eventCategory, // Ensure this is defined
            EVENT_LOCATION: eventLocation, // Ensure this is defined
            VIDEO_LIST: {
              //video_title: videoTitle,
              video_id: videoTitle,
              video_url: videoUrl,
              video_duration: Math.round(videoDuration) || 0,
              video_type: 'Vimeo',
            },
          };
  
          trackClickVideo(JSON.stringify(clickstreamDataImage));
        });
      });
    }
  };

  return (
    <Player.Container aria-label={altText} ref={playerRef}>
      <iframe
        ref={playerRef}
        src={`https://player.vimeo.com/video/${videoId}?${new URLSearchParams({
          ...playerOptions,
          player_id: videoId,
        }).toString()}`}
        width="100%"
        height="100%"
        frameBorder="0"
        allow="autoplay; fullscreen"
        allowFullScreen
        title="Vimeo Video Player"
      ></iframe>
      <Player.Overlay>
        {checkValue(posterUrl) && (
          <Cpds.Image
            alt={altText}
            src={posterUrl}
            aspectRatio={CpdsAspectRatioType.CPDS_16x9}
            objectFit={CpdsObjectFitType.CPDS_COVER}
            styleOverrides={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
              opacity: ${showThumbnail ? 1 : 0};
              transition: opacity 0.5s ease-in-out;
            `}
          />
        )}
        <Cpds.PlayButton.MutedControls
          togglePlay={handleTogglePlay}
          isPlaying={isPlaying}
          playBtnColor={playBtnColor}
        />
      </Player.Overlay>
    </Player.Container>
  );
};
export default VimeoPlayer;
